import { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, Eye, Lock, Plus, Search } from "lucide-react";
import { Button } from "@/shared/components/Button";
import { Paginacao } from "@/shared/components/Paginacao";
import { AbaInformados } from "@/modules/visitantes/components/AbaInformados";
import { AbaMeusInformados } from "@/modules/visitantes/components/AbaMeusInformados";
import { FiltrosVisitantes } from "@/modules/visitantes/components/FiltrosVisitantes";
import { LinhaVisitante } from "@/modules/visitantes/components/LinhaVisitante";
import { ModalNovaVisita } from "@/modules/visitantes/components/ModalNovaVisita";
import {
  useIndicacoes,
  useMinisteriosResponsaveis,
  usePapelVisitantes,
  usePodeInformar,
  useVisitantes,
} from "@/modules/visitantes/hooks/useVisitantes";
import type { VisitanteLista } from "@/modules/visitantes/services/visitantesService";
import {
  FILTROS_PADRAO,
  VISITANTES_POR_PAGINA,
  aplicarFiltros,
  erroIdade,
  erroPeriodo,
  filtrosDaUrl,
  filtrosParaUrl,
  temFiltroAtivo,
  type FiltrosVisitantes as Filtros,
} from "@/modules/visitantes/utils/filtros";
import { estadoDe, foraDoAcompanhamento } from "@/modules/visitantes/utils/regras";

type Aba = "visitantes" | "informados" | "meus";

/**
 * Lista do elo Visitantes. Mesma tela para todos, com o que o BANCO libera:
 * - Ministério responsável: tudo, mais cadastrar, editar, nova visita e a aba "Visitantes pendentes".
 * - Líder/gestor de outro ministério: modo consulta (sem crianças nem quem frequenta outra igreja),
 *   cadastra um Visitante pendente (o ministério responsável avalia) e acompanha "Meus pendentes".
 * - Administrador sem papel no elo: só leitura; se também lidera ou gerencia outro ministério,
 *   cadastra Visitante pendente e acompanha "Meus pendentes" como qualquer líder.
 * Esconder botão é só conveniência; cada ação é revalidada pela RPC.
 */
export function ListaVisitantesPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const { papel, carregando: carregandoPapel } = usePapelVisitantes();
  const { podeInformar, carregando: carregandoInformar } = usePodeInformar();

  const gestao = papel === "gestao";
  const admin = papel === "admin";
  const lider = papel === "lider";

  const abaPedida = searchParams.get("aba");
  const aba: Aba =
    abaPedida === "informados" && (gestao || admin)
      ? "informados"
      : abaPedida === "meus" && podeInformar
        ? "meus"
        : "visitantes";

  const filtros = useMemo(() => filtrosDaUrl(searchParams), [searchParams]);

  const { data: visitantes = [], isLoading, isError, refetch } = useVisitantes(!carregandoPapel);
  const { data: indicacoes = [] } = useIndicacoes(gestao || admin);
  const { data: responsaveis = [] } = useMinisteriosResponsaveis(lider || admin);
  const pendentes = indicacoes.filter((i) => i.situacao === "pendente").length;

  const [novaVisita, setNovaVisita] = useState<VisitanteLista | null>(null);
  const [aviso, setAviso] = useState<string | null>(
    (location.state as { aviso?: string } | null)?.aviso ?? null,
  );
  const [novosIds] = useState<string[]>(() =>
    (location.state as { novoId?: string } | null)?.novoId
      ? [(location.state as { novoId: string }).novoId]
      : [],
  );

  // O aviso vindo de "Cadastrar"/"Salvar" vive só nesta visita (não reaparece ao recarregar).
  useEffect(() => {
    const estado = location.state as { aviso?: string } | null;
    if (estado?.aviso)
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
  }, [location.pathname, location.search, location.state, navigate]);

  const filtrados = useMemo(() => aplicarFiltros(visitantes, filtros), [visitantes, filtros]);
  const totalPaginas = Math.max(1, Math.ceil(filtrados.length / VISITANTES_POR_PAGINA));
  const pagina = Math.min(filtros.pagina, totalPaginas);
  const visiveis = filtrados.slice(
    (pagina - 1) * VISITANTES_POR_PAGINA,
    pagina * VISITANTES_POR_PAGINA,
  );

  const precisam = useMemo(
    () => visitantes.filter((v) => estadoDe(v) === "precisa" && !foraDoAcompanhamento(v)).length,
    [visitantes],
  );
  const filtrosLigados = [
    filtros.situacao !== "todos",
    filtros.periodo !== "todos",
    filtros.faixa !== "todas",
  ].filter(Boolean).length;
  const aviosFiltro = erroPeriodo(filtros) || erroIdade(filtros);

  function atualizar(novos: Filtros) {
    setSearchParams(filtrosParaUrl(novos, searchParams), { replace: true });
  }

  function mudar(parcial: Partial<Filtros>) {
    // Qualquer mudança de filtro volta para a primeira página.
    atualizar({ ...filtros, ...parcial, pagina: parcial.pagina ?? 1 });
  }

  function limpar() {
    atualizar({ ...FILTROS_PADRAO });
  }

  function trocarAba(nova: Aba) {
    const proximos = new URLSearchParams();
    if (nova !== "visitantes") proximos.set("aba", nova);
    setSearchParams(proximos, { replace: true });
  }

  const abas: { chave: Aba; rotulo: string }[] = [{ chave: "visitantes", rotulo: "Visitantes" }];
  if (gestao || admin)
    abas.push({
      chave: "informados",
      rotulo: pendentes > 0 ? `Visitantes pendentes (${pendentes})` : "Visitantes pendentes",
    });
  if (podeInformar) abas.push({ chave: "meus", rotulo: "Meus pendentes" });

  if (carregandoPapel || carregandoInformar)
    return <p className="px-4 py-10 text-center text-sm text-muted">Carregando…</p>;

  return (
    <main className="mx-auto w-full max-w-2xl px-4 pb-24 pt-6 sm:px-6 sm:pt-10">
      <header className="mb-4 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl font-black tracking-tight text-primary sm:text-3xl">
            Visitantes
          </h1>
          <p className="mt-1 text-sm text-muted">
            {gestao
              ? "Você é do ministério responsável por este elo."
              : admin
                ? "Visão de administrador: somente leitura."
                : "Modo consulta: veja quem visitou e ajude no acompanhamento."}
          </p>
          {(lider || admin) && responsaveis.length > 0 && (
            <p className="mt-2 inline-flex rounded-full bg-surface-2 px-2.5 py-1 text-xs font-semibold text-muted">
              Ministério responsável: {responsaveis.join(", ")}
            </p>
          )}
        </div>
        {gestao && (
          <Button
            type="button"
            onClick={() => navigate("/visitantes/novo")}
            className="inline-flex shrink-0 items-center gap-1.5"
          >
            <Plus size={16} aria-hidden="true" /> Novo
          </Button>
        )}
        {podeInformar && (
          <Button
            type="button"
            onClick={() => navigate("/visitantes/informar")}
            className="inline-flex shrink-0 items-center gap-1.5"
          >
            <Plus size={16} aria-hidden="true" /> Novo
          </Button>
        )}
      </header>

      {lider && (
        <p className="mb-3 flex items-start gap-1.5 text-xs text-muted">
          <Lock size={14} className="mt-px shrink-0" aria-hidden="true" />
          Crianças e quem frequenta outra igreja não aparecem aqui. Os dados são para o cuidado da
          pessoa na igreja.
        </p>
      )}

      {aviso && (
        <p role="status" className="mb-3 rounded-md bg-success/10 px-4 py-3 text-sm text-success">
          {aviso}
        </p>
      )}

      {abas.length > 1 && (
        <div
          role="tablist"
          aria-label="Seções"
          className="mb-3.5 flex gap-1 rounded-xl bg-surface-2 p-1"
        >
          {abas.map((t) => (
            <button
              key={t.chave}
              type="button"
              role="tab"
              aria-selected={aba === t.chave}
              onClick={() => trocarAba(t.chave)}
              className={`min-h-9 flex-1 rounded-lg px-2 text-[13px] font-semibold transition-colors ${
                aba === t.chave ? "bg-surface text-text shadow-sm" : "text-muted hover:text-text"
              }`}
            >
              {t.rotulo}
            </button>
          ))}
        </div>
      )}

      {aba === "informados" && (
        <AbaInformados podeAvaliar={gestao} visitantes={visitantes} onAviso={setAviso} />
      )}
      {aba === "meus" && <AbaMeusInformados />}

      {aba === "visitantes" && (
        <>
          {precisam > 0 && filtros.situacao !== "precisa" && (
            <button
              type="button"
              onClick={() => mudar({ situacao: "precisa" })}
              className="mb-3 flex min-h-11 w-full items-center gap-2 rounded-xl border border-danger/40 bg-danger/10 px-4 text-left text-sm font-semibold text-danger transition-colors hover:bg-danger/15"
            >
              <AlertTriangle size={16} aria-hidden="true" className="shrink-0" />
              {precisam} {precisam === 1 ? "visitante precisa" : "visitantes precisam"} de
              acompanhamento
            </button>
          )}

          <div className="relative mb-3">
            <Search
              size={16}
              aria-hidden="true"
              className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
            />
            <input
              type="search"
              aria-label="Buscar por nome ou final do telefone"
              placeholder="Buscar por nome ou final do telefone"
              value={filtros.busca}
              maxLength={100}
              onChange={(e) => mudar({ busca: e.target.value })}
              className="w-full rounded-md border border-transparent bg-surface-2 py-2.5 pl-9 pr-4 text-sm text-text placeholder:text-muted focus:outline-none focus:ring-2 focus:ring-primary"
            />
          </div>

          <FiltrosVisitantes
            filtros={filtros}
            ativos={filtrosLigados}
            onMudar={mudar}
            onLimpar={limpar}
          />
          {aviosFiltro && (
            <p className="mb-3 text-xs text-danger">Corrija o filtro para aplicar: {aviosFiltro}</p>
          )}

          {isLoading && (
            <p className="py-10 text-center text-sm text-muted">Carregando visitantes…</p>
          )}
          {isError && (
            <p role="alert" className="py-10 text-center text-sm text-danger">
              Não foi possível carregar os visitantes.{" "}
              <button type="button" onClick={() => refetch()} className="font-semibold underline">
                Tentar de novo
              </button>
            </p>
          )}

          {!isLoading && !isError && (
            <>
              <p className="mb-2 text-xs text-muted" aria-live="polite">
                {filtrados.length} {filtrados.length === 1 ? "visitante" : "visitantes"}
                {temFiltroAtivo(filtros) ? " com os filtros escolhidos" : ""}
              </p>
              {filtrados.length === 0 ? (
                <div className="rounded-xl border border-dashed border-border px-4 py-10 text-center">
                  <Eye size={22} className="mx-auto mb-2 text-muted" aria-hidden="true" />
                  <p className="text-sm text-muted">
                    {visitantes.length === 0
                      ? gestao
                        ? "Nenhum visitante cadastrado ainda. Use “Novo” para cadastrar o primeiro."
                        : "Nenhum visitante para mostrar ainda."
                      : "Nenhum visitante com esses filtros."}
                  </p>
                  {visitantes.length > 0 && (
                    <button
                      type="button"
                      onClick={limpar}
                      className="mt-3 text-sm font-semibold text-primary underline"
                    >
                      Limpar filtros
                    </button>
                  )}
                </div>
              ) : (
                <ul className="flex flex-col gap-3">
                  {visiveis.map((v) => (
                    <LinhaVisitante
                      key={v.id}
                      visitante={v}
                      podeEscrever={gestao}
                      novo={novosIds.includes(v.id)}
                      onNovaVisita={setNovaVisita}
                    />
                  ))}
                </ul>
              )}
              <Paginacao
                paginaAtual={pagina}
                totalPaginas={totalPaginas}
                onMudarPagina={(p) => mudar({ pagina: p })}
              />
            </>
          )}
        </>
      )}

      <ModalNovaVisita
        visitante={novaVisita}
        onClose={() => setNovaVisita(null)}
        onRegistrada={(nome) => setAviso(`Visita de ${nome} registrada.`)}
      />
    </main>
  );
}
